/**
 * The small visuals on the mobile Brief's cards (issue #380).
 *
 * Each builder turns data the Brief ALREADY holds into a plain `{ kind, ... }` description
 * that `BriefVisual.jsx` draws — no request is made here. Builders are pure so the rules
 * below can be tested without rendering anything.
 *
 * The rule every builder follows: ABSENCE RETURNS null, and the card then renders exactly
 * as it did before. A missing number must never become a mark — `Number(null)` is 0 and
 * finite, so a meter drawn from it would show a confident reading of "0". `finite()` is the
 * one guard, and it rejects null / undefined / '' BEFORE converting.
 */
import { ratingFor } from '../shared/sentimentBands';
import { barScale, barShare } from '../shared/bucketCrowding';
import { rangePositionScore } from '../shared/earningsSetup';
import { finiteOrNull as finite } from './numbers';

const clampScore = (n) => Math.min(100, Math.max(0, n));

// CNN's component names, in plain sentence case. Two momentum series come back with the
// same score; only one is shown.
const INDICATOR_LABELS = {
  market_momentum_sp500: 'Market momentum',
  stock_price_strength: 'Stocks at highs vs lows',
  stock_price_breadth: 'Rising vs falling volume',
  put_call_options: 'Put vs call options',
  market_volatility: 'Market volatility',
  safe_haven_demand: 'Safe haven demand',
  junk_bond_demand: 'Junk bond demand',
};
const DUPLICATE_INDICATORS = { market_momentum_sp125: 'market_momentum_sp500' };

const indicatorLabel = (key) =>
  INDICATOR_LABELS[key] ||
  key.split('_').map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');

/**
 * Fear & Greed: the score on its 0-100 scale, where it stood a week ago, and one small
 * meter per indicator. A score of 0 is a real reading (extreme fear) and is kept; only a
 * non-number is dropped.
 */
export function sentimentVisual(current, indicators) {
  const score = finite(current?.score);
  if (score === null) return null;

  const weekAgo = finite(current?.previous_1_week);
  const rows = Object.entries(indicators || {})
    .filter(([key]) => !(DUPLICATE_INDICATORS[key] && indicators[DUPLICATE_INDICATORS[key]]))
    .map(([key, indicator]) => ({ key, label: indicatorLabel(key), score: finite(indicator?.score) }))
    .filter((row) => row.score !== null)
    .map((row) => ({ ...row, score: clampScore(row.score), rating: ratingFor(row.score) }));

  return {
    kind: 'sentimentMeter',
    score: clampScore(score),
    rating: ratingFor(score),
    previous: weekAgo === null ? null : { score: clampScore(weekAgo), label: 'a week ago' },
    indicators: rows,
  };
}

/** Breadth: advancing vs declining as two parts of one bar. */
export function splitBarVisual(up, down) {
  const advancing = finite(up);
  const declining = finite(down);
  if (advancing === null || declining === null || advancing < 0 || declining < 0) return null;
  if (advancing + declining <= 0) return null;
  return { kind: 'splitBar', up: advancing, down: declining };
}

/**
 * A mover: where today's price sits between its 52-week low and high. The position and its
 * guards (a missing or degenerate range is null, never a midpoint) are the Expectations
 * Bar's `rangePositionScore` — one definition of "where in its range is this stock".
 */
export function rangeBarVisual(company) {
  const low = finite(company?.fiftyTwoWeekLow);
  const high = finite(company?.fiftyTwoWeekHigh);
  const score = rangePositionScore(finite(company?.stockPrice), low, high);
  return score === null ? null : { kind: 'rangeBar', position: score / 100, low, high };
}

/**
 * Asset classes: the called-out groups as bars either side of a centre line. Bar LENGTH is
 * how unusual the move is for that group (the same scale the desktop tab uses, over ALL
 * rows), the printed number is the move itself — exactly the desktop tab's split.
 *
 * @param rows every row of the window (already through withBucketMeta)
 */
export function bucketBarsVisual(rows) {
  const called = (rows || []).filter((r) => r.callout && finite(r.z) !== null && finite(r.movePct) !== null);
  if (!called.length) return null;
  const scale = barScale(rows);
  return {
    kind: 'divergingBars',
    rows: called
      .slice()
      .sort((a, b) => b.z - a.z)
      .map((r) => ({ key: r.ticker, label: r.label, share: barShare(r.z, scale), positive: r.z >= 0, movePct: r.movePct })),
  };
}

/**
 * A count split into named groups, drawn as one bar (issue #387: guidance raised / kept /
 * lowered). Bar LENGTH is each group's share of the total; the printed numbers are the counts.
 * A group with a zero count keeps its legend entry but draws nothing, so "0 lowered" is said,
 * not hidden. Null when nothing was counted — a bar with no length says nothing.
 *
 * @param segments [{ key, label, count, color }] in draw order
 */
export function stackedBarVisual(segments) {
  const rows = (segments || [])
    .map((s) => ({ ...s, count: finite(s?.count) }))
    .filter((s) => s.count !== null && s.count >= 0 && typeof s.label === 'string' && s.label);
  if (!rows.length) return null;
  const total = rows.reduce((sum, s) => sum + s.count, 0);
  if (total <= 0) return null;
  return { kind: 'stackedBar', segments: rows.map(({ key, label, count, color }) => ({ key, label, count, color })) };
}
